import type { ProFormInstance } from '@ant-design/pro-components';
import {
  ModalForm,
  ProForm,
  ProFormCheckbox,
  ProFormDatePicker,
  ProFormDateRangePicker,
  ProFormDateTimePicker,
  ProFormDateTimeRangePicker,
  ProFormDigit,
  ProFormRadio,
  ProFormSelect,
  ProFormSwitch,
  ProFormText,
  ProFormTextArea,
  ProFormUploadButton,
} from '@ant-design/pro-components';
import { Button, Space } from 'antd';
import React, { useEffect, useRef } from 'react';
import type { CommonModalFormProps, ModalFormFieldConfig } from './types';

const CommonModalForm: React.FC<CommonModalFormProps> = (props) => {
  const {
    title = '新建',
    width = '600px',
    open = false,
    onOpenChange,
    fields,
    initialValues,
    onFinish,
    onCancel,
    submitText = '确定',
    cancelText = '取消',
    showCancel = true,
    showSubmit = true,
    customActions = [],
    onBeforeSubmit,
    onAfterSubmit,
    closeOnSuccess = true,
    resetOnClose = true,
    layout = 'horizontal',
    labelCol = { span: 6 },
    wrapperCol = { span: 18 },
    ...restProps
  } = props;

  const formRef = useRef<ProFormInstance | null>(null);

  // 重置表单
  useEffect(() => {
    if (!open && resetOnClose && formRef.current) {
      formRef.current.resetFields();
    }
  }, [open, resetOnClose]);

  // 渲染表单字段
  const renderFormField = (field: ModalFormFieldConfig) => {
    const {
      name,
      label,
      type = 'text',
      required = false,
      placeholder,
      formItemProps,
      render,
      options = [],
      initialValue,
      width: fieldWidth,
      rules = [],
      disabled = false,
      readonly = false,
      tooltip,
      extraProps = {},
    } = field;

    // 构建基础属性
    // 合并 rules：优先使用 formItemProps 中的 rules，如果没有则使用字段配置的 rules
    const finalRules =
      formItemProps?.rules ||
      (required
        ? [{ required: true, message: `请输入${label}` }, ...rules]
        : rules);

    const baseProps: Record<string, any> = {
      name,
      label,
      placeholder: placeholder || `请输入${label}`,
      disabled: disabled || readonly,
      tooltip,
      initialValue,
      rules: finalRules,
      ...formItemProps,
      ...extraProps,
    };

    // 默认不传 width，让表单项随弹框宽度自适应；如显式配置 field.width 则尊重
    if (fieldWidth) {
      baseProps.width = fieldWidth;
    }

    // 如果有自定义渲染，使用自定义渲染（包裹 ProForm.Item 以保留 label）
    if (render) {
      return (
        <ProForm.Item
          name={name}
          label={label}
          rules={finalRules}
          initialValue={initialValue}
          tooltip={tooltip}
          {...formItemProps}
        >
          {render()}
        </ProForm.Item>
      );
    }

    // 根据类型渲染不同的表单项
    switch (type) {
      case 'textarea':
        return <ProFormTextArea {...baseProps} />;
      case 'number':
        return <ProFormDigit {...baseProps} />;
      case 'select':
        return (
          <ProFormSelect
            {...baseProps}
            options={options}
            placeholder={placeholder || `请选择${label}`}
            mode={extraProps?.mode}
          />
        );
      case 'radio':
        return (
          <ProFormRadio.Group
            {...baseProps}
            options={options}
            placeholder={placeholder || `请选择${label}`}
          />
        );
      case 'checkbox':
        return (
          <ProFormCheckbox.Group
            {...baseProps}
            options={options}
            placeholder={placeholder || `请选择${label}`}
          />
        );
      case 'date':
        return <ProFormDatePicker {...baseProps} />;
      case 'dateTime':
        return <ProFormDateTimePicker {...baseProps} />;
      case 'dateRange':
        return <ProFormDateRangePicker {...baseProps} />;
      case 'dateTimeRange':
        return <ProFormDateTimeRangePicker {...baseProps} />;
      case 'switch':
        return <ProFormSwitch {...baseProps} />;
      case 'upload':
        // React 19 + TS 下，ProFormUploadButton 类型可能导致 JSX 报错
        return React.createElement(ProFormUploadButton as any, baseProps);
      case 'password':
        return <ProFormText.Password {...baseProps} />;
      case 'text':
      default:
        return <ProFormText {...baseProps} />;
    }
  };

  // 处理提交
  const handleFinish = async (values: Record<string, any>) => {
    try {
      // 提交前验证
      if (onBeforeSubmit) {
        const canSubmit = await Promise.resolve(onBeforeSubmit(values));
        if (!canSubmit) {
          return false;
        }
      }

      // 执行提交
      let success = true;
      if (onFinish) {
        success = await Promise.resolve(onFinish(values));
      }

      // 提交后回调
      if (onAfterSubmit) {
        onAfterSubmit(success, values);
      }

      // 成功后关闭弹窗
      if (success && closeOnSuccess) {
        onOpenChange?.(false);
      }

      return success;
    } catch (error) {
      console.error('Form submit error:', error);
      return false;
    }
  };

  // 处理取消
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange?.(false);
    }
  };

  // 构建操作按钮
  const buildActions = () => {
    const actions: React.ReactNode[] = [];

    // 取消按钮
    if (showCancel) {
      actions.push(
        <Button key="cancel" onClick={handleCancel}>
          {cancelText}
        </Button>,
      );
    }

    // 提交按钮
    if (showSubmit) {
      actions.push(
        <Button key="submit" type="primary" htmlType="submit">
          {submitText}
        </Button>,
      );
    }

    // 自定义操作按钮
    customActions.forEach((action, index) => {
      if (action.visible !== false) {
        actions.push(
          <Button
            key={action.label || index}
            type={action.type}
            danger={action.danger}
            disabled={action.disabled}
            icon={action.icon}
            onClick={async () => {
              if (action.onClick && formRef.current) {
                const values = await formRef.current.validateFields();
                await action.onClick(values);
              }
            }}
          >
            {action.label}
          </Button>,
        );
      }
    });

    return actions.length > 0 ? <Space>{actions}</Space> : null;
  };

  return (
    <ModalForm
      title={title}
      width={width}
      open={open}
      onOpenChange={onOpenChange}
      onFinish={handleFinish}
      initialValues={initialValues}
      formRef={formRef}
      layout={layout}
      labelCol={labelCol}
      wrapperCol={wrapperCol}
      modalProps={{
        destroyOnClose: true,
        ...restProps.modalProps,
      }}
      submitter={{
        resetButtonProps: {
          style: { display: 'none' },
        },
        render: (_, defaultSubmitter) => {
          const actions: React.ReactNode[] = [];

          // 取消按钮
          if (showCancel) {
            actions.push(
              <Button key="cancel" onClick={handleCancel}>
                {cancelText}
              </Button>,
            );
          }

          // 提交按钮 - 使用 formRef.submit 触发表单提交
          if (showSubmit) {
            actions.push(
              <Button
                key="submit"
                type="primary"
                onClick={() => {
                  formRef.current?.submit();
                }}
              >
                {submitText}
              </Button>,
            );
          }

          // 自定义操作按钮
          customActions.forEach((action, index) => {
            if (action.visible !== false) {
              actions.push(
                <Button
                  key={action.label || index}
                  type={action.type}
                  danger={action.danger}
                  disabled={action.disabled}
                  icon={action.icon}
                  onClick={async () => {
                    if (action.onClick && formRef.current) {
                      const values = await formRef.current.validateFields();
                      await action.onClick(values);
                    }
                  }}
                >
                  {action.label}
                </Button>,
              );
            }
          });

          return actions.length > 0 ? <Space>{actions}</Space> : null;
        },
      }}
      {...restProps}
    >
      {fields.map((field, index) => (
        <React.Fragment key={field.name || index}>
          {renderFormField(field)}
        </React.Fragment>
      ))}
    </ModalForm>
  );
};

export default CommonModalForm;
export type {
  CommonModalFormProps,
  ModalFormActionConfig,
  ModalFormFieldConfig,
} from './types';
